import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/dnh/SiteChrome";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";

  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  // If no token in URL, show error immediately
  const noToken = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPwd.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (newPwd !== confirmPwd) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, new_password: newPwd }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Reset failed — the link may have expired.");
      } else {
        setDone(true);
        setTimeout(() => navigate("/signin"), 3000);
      }
    } catch {
      setError("Network error — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]" data-testid="reset-password-page">
      <SiteHeader activeNav="" />

      <main className="flex items-center justify-center min-h-[calc(100vh-120px)] px-5 py-12">
        <div className="w-full max-w-[400px]">

          {noToken ? (
            <div className="text-center" data-testid="reset-no-token">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-50 mb-5">
                <XCircle size={28} strokeWidth={1.5} className="text-[var(--crimson)]" />
              </div>
              <h1 className="font-serif text-[28px] text-[var(--ink)] mb-3">Invalid reset link</h1>
              <p className="font-sans text-[14px] text-[var(--ink-2)] mb-6">This link is missing a reset token. Please request a new one.</p>
              <Link to="/forgot-password" className="inline-flex items-center gap-2 font-sans text-[14px] text-[var(--crimson)] hover:underline">
                Request new link <ArrowRight size={14} />
              </Link>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-50 mb-5">
                <CheckCircle2 size={28} strokeWidth={1.5} className="text-green-600" />
              </div>
              <h1 className="font-serif text-[32px] leading-[1.1] text-[var(--ink)] mb-3">Password updated</h1>
              <p className="font-sans text-[14.5px] text-[var(--ink-2)] mb-2">Redirecting you to sign in…</p>
              <Link to="/signin" className="font-sans text-[14px] text-[var(--crimson)] hover:underline">Sign in now &rarr;</Link>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--muted)] mb-3">
                  Daily News Highlights
                </div>
                <h1 className="font-serif text-[34px] leading-[1.05] tracking-[-0.015em] text-[var(--ink)]">
                  Set a new password
                </h1>
                <p className="font-sans text-[14.5px] leading-[1.5] text-[var(--ink-2)] mt-2">
                  Choose a new password for your account.
                </p>
              </div>

              {error && (
                <div className="mb-4 flex items-start gap-2.5 rounded-[8px] border border-[var(--crimson)]/30 bg-[var(--crimson)]/5 px-4 py-3" data-testid="reset-error">
                  <AlertCircle size={15} strokeWidth={2} className="text-[var(--crimson)] shrink-0 mt-[1px]" />
                  <p className="font-sans text-[13px] text-[var(--crimson)] leading-[1.5]">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="reset-form" className="space-y-4">
                <div>
                  <label htmlFor="reset-new-pwd" className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)] mb-1.5">
                    New password
                  </label>
                  <div className="relative">
                    <input
                      id="reset-new-pwd"
                      type={showPwd ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Min. 8 characters"
                      value={newPwd}
                      onChange={(e) => setNewPwd(e.target.value)}
                      data-testid="reset-new-password-input"
                      className="w-full h-12 px-4 pr-11 border border-[var(--hairline)] rounded-[10px] bg-[var(--paper)] font-sans text-[15px] text-[var(--ink)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--ink)] transition-colors"
                    />
                    <button type="button" onClick={() => setShowPwd(v => !v)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--ink)] transition-colors">
                      {showPwd ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="reset-confirm-pwd" className="block font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)] mb-1.5">
                    Confirm new password
                  </label>
                  <input
                    id="reset-confirm-pwd"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    data-testid="reset-confirm-password-input"
                    className="w-full h-12 px-4 border border-[var(--hairline)] rounded-[10px] bg-[var(--paper)] font-sans text-[15px] text-[var(--ink)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--ink)] transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit-button"
                  className="w-full h-12 rounded-full bg-[var(--ink)] text-[var(--paper)] font-sans text-[14px] font-medium flex items-center justify-center gap-2 hover:bg-[var(--crimson)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-1"
                >
                  {loading ? "Saving…" : "Set new password"}
                  {!loading && <ArrowRight size={15} strokeWidth={2} />}
                </button>
              </form>

              <p className="mt-6 text-center font-sans text-[13px] text-[var(--muted)]">
                Remembered it?{" "}
                <Link to="/signin" className="text-[var(--ink)] hover:text-[var(--crimson)] transition-colors">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
